import { useUser } from '@auth0/nextjs-auth0/client';
import useGetUser from '../queries/user/useGetUser';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api/api';
import { StepService } from '@/stores/slices/step-service/step-services.types';
import { useRootStore } from '@/providers/root-store-provider';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Service } from '@/services/api/services-api';

type Props = {
  form: UseFormReturn<any>; // TODO: Add type
  service: Service;
};

export const useFormParticipants = ({ form, service }: Props) => {
  const [isOpenRegisteredUserModal, setIsOpenRegisteredUserModal] =
    useState(false);
  const { user: userAuth0 } = useUser();
  const { data: userData, isPending: isPendingUserData } = useGetUser(
    userAuth0?.email
  );
  const currentStep = useRootStore((store) => store.currentStep);
  const removeStep = useRootStore((store) => store.removeStep);
  const stepServicesData: StepService | null = useRootStore(
    (store) => store.stepServicesData
  );
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );
  const [accordionValue, setAccordionValue] = useState(() => {
    if (userAuth0 && stepServicesData?.participants) {
      form.trigger('user');
      return 'participant.0';
    } else {
      return 'participant.user';
    }
  });
  const userEmailRef = useRef('');
  const emailHint = userEmailRef.current.replace(/\+/g, '%2B');
  const {
    data: isUserExists,
    isSuccess,
    refetch: checkUserEmail,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['checkUser'],
    queryFn: () => api.user.userExistsByEmail(userEmailRef.current),
    enabled: false
  });

  const userEmailField = form.watch('user.email');
  const isError = Boolean(Object.keys(form.formState.errors).length);

  const participants = useWatch({
    control: form.control,
    name: 'participants'
  });

  const isUserParticipant = useWatch({
    control: form.control,
    name: 'user.userIsParticipant'
  });

  const isMaxParticipants =
    (isUserParticipant ? participants.length + 1 : participants.length) >=
    service.maxConsumers;
  const isMinParticipants =
    (isUserParticipant ? participants.length + 1 : participants.length) ===
    service.minConsumers;

  const onBack = () => {
    removeStep(currentStep);
  };

  const handleBlur = async () => {
    const isEmailValid = await form.trigger('user.email');
    if (userEmailField !== '' && isEmailValid) {
      userEmailRef.current = userEmailField;
      checkUserEmail();
    }
    loadStepServicesData({ email: userEmailField });
  };

  useEffect(() => {
    if (isSuccess && isUserExists && !userData?.email) {
      setIsOpenRegisteredUserModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  return {
    currentStep,
    userData,
    emailHint,
    stepServicesData,
    accordionValue,
    participants,
    isUserParticipant,
    isError,
    isMaxParticipants,
    isMinParticipants,
    isOpenRegisteredUserModal,
    setIsOpenRegisteredUserModal,
    checkUserEmail,
    handleBlur,
    setAccordionValue,
    removeStep,
    onBack
  };
};
